import { Disposable, autoinject, PLATFORM } from 'aurelia-framework';
import { Router, RouterConfiguration } from 'aurelia-router';
import { IDemoRouteConfig } from 'app/demo/lib/router-types';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { EventAggregator } from 'aurelia-event-aggregator';

@autoinject()
export class DemoEssHorizontalNavMenuPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected router!: Router;
    protected currentRoute: string = '';

    protected useFolderTabStyling = false;

    private navigationCompletedEvent: Disposable | null = null;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        router: Router,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
        this.router = router;
    }

    protected attached(): void {
        this.navigationCompletedEvent = this.events.subscribe('router:navigation:complete', () => this.navigationComplete());
        this.navigationComplete();
    }

    ////****************** Events ***************************************************////
    protected navigationComplete(): void {
        const routeConfig = this.router.currentInstruction.config;
        const route = Array.isArray(routeConfig.route) ? routeConfig.route[0] : routeConfig.route;
        const tab = routeConfig.settings && (routeConfig.settings.tab as string) || '';
        if(routeConfig && (routeConfig.nav || tab)) {
            this.currentRoute = `/${tab || route}`;
        }
    }

    ////****************** Functions ************************************************////

    protected generateTabLink(): string {
        const parentUrl = this.router.parent.generate('demo');
        return parentUrl;
    }

    protected async configureRouter(config: RouterConfiguration, router: Router): Promise<void> {
        const routes: IDemoRouteConfig[] = [
            {
                route: '', redirect: 'ess-autocomplete'
            },
            {
                route: 'ess-autocomplete', name: 'demo.elements.ess-autocomplete', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-autocomplete-page'), title: 'autocomplete', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-autocomplete.html' }
            },
            {
                route: 'ess-badge', name: 'demo.elements.ess-badge', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-badge-page'), title: 'badge', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-badge.html' }
            },
            {
                route: 'ess-button', name: 'demo.elements.ess-button', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-button-page'), title: 'button', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-button.html' }
            },
            {
                route: 'ess-card', name: 'demo.elements.ess-card', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-card-page'), title: 'card', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-card.html' }
            },
            {
                route: 'ess-checkbox', name: 'demo.elements.ess-checkbox', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-checkbox-page'), title: 'checkbox', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-checkbox.html' }
            },
            {
                route: 'ess-checklist-element', name: 'demo.elements.ess-checklist-element', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-checklist-element-page'), title: 'checklist-element', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-checklist-element.html' }
            },
            {
                route: 'ess-confirm', name: 'demo.elements.ess-confirm', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-confirm-page'), title: 'confirm', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/forms/ess-confirm.html' }
            },
            {
                route: 'ess-context-menu', name: 'demo.elements.ess-context-menu', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-context-menu-page'), title: 'context-menu', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/forms/ess-context-menu.html' }
            },
            {
                route: 'ess-currency', name: 'demo.elements.ess-currency', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-currency-page'), title: 'currency', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-currency.html' }
            },
            {
                route: 'ess-date', name: 'demo.elements.ess-date', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-date-page'), title: 'date', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-date.html' }
            },
            {
                route: 'ess-daterange', name: 'demo.elements.ess-daterange', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-daterange-page'), title: 'daterange', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-daterange.html' }
            },
            {
                route: 'ess-datetime', name: 'demo.elements.ess-datetime', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-datetime-page'), title: 'datetime', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-datetime.html' }
            },
            {
                route: 'ess-dialog', name: 'demo.elements.ess-dialog', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-dialog-page'), title: 'dialog', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-dialog.html' }
            },
            {
                route: 'ess-dynamic-grid', name: 'demo.elements.ess-dynamic-grid', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-dynamic-grid-page'), title: 'dynamic-grid', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-dynamic-grid.html' }
            },
            {
                route: 'ess-dropdown', name: 'demo.elements.ess-dropdown', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-dropdown-page'), title: 'dropdown', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/forms/ess-dropdown.html' }
            },
            {
                route: 'ess-file', name: 'demo.elements.ess-file', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-file-page'), title: 'file', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/forms/ess-file.html' }
            },
            {
                route: 'ess-file-progress', name: 'demo.elements.ess-file-progress', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-file-progress-page'), title: 'file-progress', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-file-progress.html' }
            },
            {
                route: 'ess-filter', name: 'demo.elements.ess-filter', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-filter-page'), title: 'filter', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-filter.html' }
            },
            {
                route: 'ess-form', name: 'demo.elements.ess-form', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-form-page'), title: 'form', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-form.html' }
            },
            {
                route: 'ess-horizontal-nav-menu', name: 'demo.elements.ess-horizontal-nav-menu', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-horizontal-nav-menu-page'), title: 'horizontal-nav-menu', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-horizontal-nav-menu.html' }
            },
            {
                route: 'ess-info', name: 'demo.elements.ess-info', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-info-page'), title: 'info', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-info.html' }
            },
            {
                route: 'ess-integer', name: 'demo.elements.ess-integer', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-integer-page'), title: 'integer', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-integer.html' }
            },
            {
                route: 'ess-logo', name: 'demo.elements.ess-logo', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-logo-page'), title: 'logo', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/forms/ess-logo.html' }
            },
            {
                route: 'ess-notifications', name: 'demo.elements.ess-notifications', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-notifications-page'), title: 'notifications', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/forms/ess-notifications.html' }
            },
            {
                route: 'ess-number', name: 'demo.elements.ess-number', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-number-page'), title: 'number', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-number.html' }
            },
            {
                route: 'ess-pager', name: 'demo.elements.ess-pager', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-pager-page'), title: 'pager', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-pager.html' }
            },
            {
                route: 'ess-radio', name: 'demo.elements.ess-radio', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-radio-page'), title: 'radio', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-radio.html' }
            },
            {
                route: 'ess-rule', name: 'demo.elements.ess-rule', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-rule-page'), title: 'rule', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-rule.html' }
            },
            {
                route: 'ess-select', name: 'demo.elements.ess-select', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-select-page'), title: 'select', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-select.html' }
            },
            {
                route: 'ess-sortable-list', name: 'demo.elements.ess-sortable-list', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-sortable-list-page'), title: 'sortable-list', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-sortable-list.html' }
            },
            {
                route: 'ess-switch', name: 'demo.elements.ess-switch', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-switch-page'), title: 'switch', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-switch.html' }
            },
            {
                route: 'ess-table', name: 'demo.elements.ess-table', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-table-page'), title: 'table', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/forms/ess-table.html' }
            },
            {
                route: 'ess-tabs-horizontal', name: 'demo.elements.ess-tabs-horizontal', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-tabs-horizontal-page'), title: 'tabs-horizontal', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-tabs-horizontal.html' }
            },
            {
                route: 'ess-tabs-vertical', name: 'demo.elements.ess-tabs-vertical', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-tabs-vertical-page'), title: 'tabs-vertical', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-tabs-vertical.html' }
            },
            {
                route: 'ess-text', name: 'demo.elements.ess-text', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-text-page'), title: 'text', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-text.html' }
            },
            {
                route: 'ess-textarea', name: 'demo.elements.ess-textarea', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-textarea-page'), title: 'textarea', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-textarea.html' }
            },
            {
                route: 'ess-time', name: 'demo.elements.ess-time', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-time-page'), title: 'time', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/ess-time.html' }
            },
            {
                route: 'ess-tooltip', name: 'demo.elements.ess-tooltip', moduleId: PLATFORM.moduleName('app/demo/pages/elements/demo-ess-tooltip-page'), title: 'tooltip', nav: true,
                settings: { isVisible: true, bypassAuthentication: true, helpFragment: 'gui/shared/elements/forms/ess-tooltip.html' }
            }
        ];

        config.map(routes);

        config.mapUnknownRoutes('app/shared/components/not-found');

        this.router = router;
    }
}
